import { Button, Grid } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import PhoneField from "components/@phone-field";
import TextField from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import useText from "constants/text";
import { CompanyUser, Profile_User } from "generated/graphql";
import { useCountryCode } from "hooks/useCurrentUser";
import React from "react";
import "styled-components/macro";

export interface UpdateProfilProps {
  user: CompanyUser;
  handlerUpdate: (profile: CompanyUser) => void;
  isUpdate: boolean;
  cancel?: () => void;
  resetPassword: () => void;
  update: any;
}
const defaultValue: CompanyUser = {
  company_user_email: "",
  company_user_phone_number: "",
  company_user_lastname: "",
  company_user_firstname: "",
  company_user_job: "",
  company_user_profil: Profile_User.Simple,
};

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: "54px 54px",
      marginTop: 0,
      flexFlow: "row wrap",
      "@media(max-width: 1256px)": {
        display: "flex",
        marginTop: 40,
      },
    },
    center: {
      marginLeft: "auto",
      marginRight: "auto",
      "&.MuiGrid-grid-xs-6": {
        "@media(max-width: 1256px)": {
          maxWidth: "100%",
        },
      },
    },
    buttons: {
      position: "absolute",
      right: 40,
      bottom: 140,
      marginTop: 40,
      "@media(max-width: 1256px)": {
        display: "flex",
        right: "30%",
      },
    },
  })
);

export function UpdateProfil({ user, handlerUpdate, cancel, isUpdate, resetPassword, update }: UpdateProfilProps) {
  const [formValues, setFormValues] = React.useState<CompanyUser>(defaultValue);
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const styles = useStyles();
  const { text } = useText();
  const countryCode = useCountryCode();
  const handleInputChange = React.useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  const handleConfirm = () => {
    setFormSubmited(true);
    if (!isValidPhoneNumberFormat(formValues.company_user_phone_number)) return;
    // eslint-disable-next-line no-restricted-syntax
    for (const el in formValues)
      if (
        (el === "company_user_firstname" || el === "company_user_lastname" || el === "company_user_email") &&
        formValues[el]?.length === 0
      )
        return;
    handlerUpdate(
      formValues.company_user_phone_number.length !== 0
        ? {
            ...formValues,
            company_user_phone_number: `${formValues.company_user_phone_number}`,
          }
        : formValues
    );
    setFormSubmited(false);
  };

  React.useEffect(() => {
    if (user != null) {
      setFormValues({
        ...user,
      });
    } else {
      setFormValues(defaultValue);
    }
  }, [user]);

  const isValidPhone = () =>
    formValues.company_user_phone_number?.length && isValidPhoneNumberFormat(formValues.company_user_phone_number);

  return (
    <Grid container spacing={4} style={{ padding: "0px 54px", marginTop: 0 }}>
      <Grid item xs={6}>
        <TextField
          disabled={!isUpdate}
          fullWidth
          label={text.all.first_name}
          name="company_user_firstname"
          value={formValues?.company_user_firstname}
          onChange={handleInputChange}
          error={isFormSubmited && formValues?.company_user_firstname?.length === 0}
          helperText={
            isFormSubmited && formValues?.company_user_firstname?.length === 0
              ? `${text.user.this_field_is_required}`
              : ""
          }
          required
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          disabled={!isUpdate}
          fullWidth
          label={text.all.last_name}
          name="company_user_lastname"
          value={formValues?.company_user_lastname}
          onChange={handleInputChange}
          error={isFormSubmited && formValues?.company_user_lastname?.length === 0}
          helperText={
            isFormSubmited && formValues?.company_user_lastname?.length === 0
              ? `${text.user.this_field_is_required}`
              : ""
          }
          required
        />
      </Grid>
      <Grid item xs={6}>
        <PhoneField
          countryCode={countryCode}
          disabled={!isUpdate}
          fullWidth
          label={text.all.phone_number}
          name="company_user_phone_number"
          value={formValues?.company_user_phone_number}
          onValueChange={(value) => {
            setFormValues({
              ...formValues,
              company_user_phone_number: value,
            });
          }}
          error={isFormSubmited && !isValidPhone()}
          helperText={isFormSubmited && !isValidPhone() ? `${text.user.this_field_is_required}` : ""}
          required
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          disabled
          fullWidth
          label={text.all.email}
          name="company_user_email"
          type="email"
          value={formValues?.company_user_email}
          onChange={handleInputChange}
          error={isFormSubmited && formValues?.company_user_email?.length === 0}
          helperText={
            isFormSubmited && formValues?.company_user_email?.length === 0 ? `${text.user.this_field_is_required}` : ""
          }
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          disabled={!isUpdate}
          fullWidth
          label={text.user.user_role}
          name="company_user_job"
          type="text"
          value={formValues?.company_user_job}
          onChange={handleInputChange}
        />
      </Grid>
      {!isUpdate ? (
        <Grid item xs={12} className={styles.buttons}>
          <Button
            color="primary"
            css={`
              &.MuiButton-root {
                width: auto;
                margin-right: 30px;
              }
            `}
            onClick={resetPassword}
          >
            {text.user.change_password}
          </Button>
          <Button
            color="primary"
            variant="contained"
            css={`
              &.MuiButton-root {
                width: 160px;
              }
            `}
            onClick={update}
          >
            {text.user.edit_profile}
          </Button>
        </Grid>
      ) : (
        <Grid item xs={12} className={styles.buttons}>
          <Button
            color="primary"
            variant="outlined"
            css={`
              &.MuiButton-root {
                width: 160px;
                margin-right: 30px;
              }
            `}
            onClick={() => {
              setFormValues(user);
              cancel();
            }}
          >
            {text.all.cancel}
          </Button>
          <Button
            color="primary"
            variant="contained"
            css={`
              &.MuiButton-root {
                width: 160px;
              }
            `}
            onClick={handleConfirm}
          >
            {text.all.update}
          </Button>
        </Grid>
      )}
    </Grid>
  );
}
